import React, { useState, useEffect } from 'react';
import userService from '../services/userService';

const AssignCourseModal = ({ user, isOpen, onClose, onAssign }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchError, setFetchError] = useState('');

  // Fetch available courses for user when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchCourses();
    }
  }, [isOpen, user]);

  const fetchCourses = async () => {
    try {
      setFetchError('');
      // Fetch only courses that are NOT already assigned to this user
      const data = await userService.getAvailableCoursesForUser(user.id);
      setCourses(data);
    } catch (err) {
      console.error('Failed to fetch available courses:', err);
      setFetchError('Failed to load available courses. Please try again.');
    }
  };

  if (!isOpen || !user) return null;

  const handleAssign = async () => {
    if (!selectedCourseId) {
      setError('Please select a course');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onAssign(user.id, parseInt(selectedCourseId));
      setSelectedCourseId('');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to assign course');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCourseId('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900">Assign Course</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* User Info */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Assigning course to:</p>
            <p className="font-semibold text-slate-900 text-lg">{user.username}</p>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>

          {/* Fetch Error Message */}
          {fetchError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{fetchError}</p>
              <button
                onClick={fetchCourses}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Course Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Course
            </label>
            {courses.length === 0 && !fetchError ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  All available courses have already been assigned to this user.
                </p>
              </div>
            ) : (
              <select
                value={selectedCourseId}
                onChange={(e) => {
                  setSelectedCourseId(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading || fetchError}
              >
                <option value="">-- Select a course --</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title} - ₹{course.price}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Selected Course Preview */}
          {selectedCourseId && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              {(() => {
                const selectedCourse = courses.find(c => c.id === parseInt(selectedCourseId));
                return selectedCourse ? (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">{selectedCourse.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{selectedCourse.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="text-gray-600">
                        <strong>Price:</strong> ₹{selectedCourse.price}
                      </span>
                      {selectedCourse.module_count !== undefined && (
                        <span className="text-gray-600">
                          <strong>Modules:</strong> {selectedCourse.module_count}
                        </span>
                      )}
                      {selectedCourse.lesson_count !== undefined && (
                        <span className="text-gray-600">
                          <strong>Lessons:</strong> {selectedCourse.lesson_count}
                        </span>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={isLoading || !selectedCourseId}
          >
            {isLoading ? 'Assigning...' : 'Assign Course'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignCourseModal;
